import React from "react";
import {
  konferensiInternasionalData,
  conferenceCategories,
} from "./data/konferensiInternasionalData";

const KonferensiInternasional: React.FC = () => {
  const getImageDescription = (conferenceName: string): string => {
    return `Dokumentasi kegiatan ${conferenceName}`;
  };

  const getCategoryColor = (category: string): string => {
    const categoryData = conferenceCategories.find(
      (cat) => cat.name === category,
    );
    return categoryData?.color || "text-base-content";
  };

  return (
    <div>
      <div className="mx-auto w-11/12 laptop:max-w-full desktop:max-w-7xl">
        <div className="flex flex-row my-12">
          <div className="p-4 w-full">
            <div className="card w-full bg-base-100 shadow-xl border-t-8 border-blue-600 rounded-xl">
              <div className="card-body p-0">
                <div className="flex flex-row px-6 pt-6">
                  <div className="grow">
                    <h2 className="card-title text-3xl font-bold text-base-content">
                      Konferensi Internasional
                    </h2>
                    <p className="text-base-content/70 mt-2">
                      Dokumentasi Kegiatan Konferensi dan Pertemuan
                      Internasional Kepolisian
                    </p>
                  </div>
                  <div className="shrink">
                    <div className="text-sm breadcrumbs">
                      <ul>
                        <li>
                          <a
                            href="/"
                            className="text-base-content/60 hover:text-base-content"
                          >
                            Beranda
                          </a>
                        </li>
                        <li className="text-base-content/60">
                          Kerja Sama Internasional
                        </li>
                        <li className="text-primary font-semibold">
                          Konferensi Internasional
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="divider m-0"></div>

                <div className="p-6">
                  {/* Introduction */}
                  <div className="bg-base-200 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Bagian Konferensi Internasional
                    </h3>
                    <p className="text-base-content/70 leading-relaxed mb-4">
                      Bagian Konferensi Internasional merupakan salah satu unsur
                      pelaksana dalam struktur Divisi Hubungan Internasional
                      (Divhubinter) Mabes Polri yang berada di bawah koordinasi
                      Biro Kerja Sama Internasional (Rokersin). Bagian ini
                      memiliki peran strategis dalam mendukung keterlibatan
                      aktif Polri di forum-forum internasional, khususnya yang
                      berkaitan dengan kerja sama penegakan hukum lintas negara.
                    </p>
                    <p className="text-base-content/70 leading-relaxed">
                      Bagian Konferensi Internasional terdiri dari dua
                      Sub-bagian: Multilateral dan Bilateral. Sub-Bagian
                      Bilateral bertugas mengelola hubungan kerja sama antara
                      Polri dengan satu negara mitra secara langsung, sementara
                      Sub-Bagian Multilateral menangani kerja sama Polri dengan
                      berbagai negara atau organisasi internasional secara
                      kolektif dalam forum regional maupun global seperti
                      ASEANAPOL, INTERPOL, UNODC.
                    </p>
                  </div>

                  {/* Conferences */}
                  <div className="space-y-8">
                    {konferensiInternasionalData.map((conference, index) => (
                      <div key={index} className="bg-base-200 rounded-xl p-6">
                        <div className="flex flex-col lg:flex-row gap-6">
                          {/* Content Section */}
                          <div
                            className={`${conference.images ? "lg:w-2/3" : "w-full"}`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                              <h3 className="text-xl font-bold text-base-content">
                                {conference.name}
                              </h3>
                              <span
                                className={`badge badge-lg ${getCategoryColor(conference.category)}`}
                              >
                                {conference.category}
                              </span>
                            </div>

                            <p className="text-base-content/70 leading-relaxed mb-4">
                              {conference.description}
                            </p>

                            {conference.additionalInfo && (
                              <div className="bg-base-100 rounded-lg p-4 mb-4">
                                <h4 className="font-semibold text-base-content mb-2">
                                  {conference.additionalInfo}
                                </h4>
                              </div>
                            )}
                          </div>

                          {/* Images Section */}
                          {conference.images &&
                            conference.images.length > 0 && (
                              <div className="lg:w-1/3">
                                <div className="grid gap-3">
                                  {conference.images.map((image, imgIndex) => (
                                    <div
                                      key={imgIndex}
                                      className="bg-base-100 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                                    >
                                      <img
                                        src={image}
                                        alt={`${conference.name} - ${getImageDescription(conference.name)}`}
                                        loading="lazy"
                                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                                        onError={(e) => {
                                          const target =
                                            e.target as HTMLImageElement;
                                          target.src = "/placeholder-image.jpg";
                                          target.alt = "Gambar tidak tersedia";
                                        }}
                                        onClick={() =>
                                          window.open(image, "_blank")
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Statistics */}
                  <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Ringkasan Kegiatan
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-primary">
                          13
                        </div>
                        <div className="text-sm text-base-content/70">
                          Total Konferensi
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-secondary">
                          3
                        </div>
                        <div className="text-sm text-base-content/70">
                          Kategori Kegiatan
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-success">
                          10
                        </div>
                        <div className="text-sm text-base-content/70">
                          Negara ASEAN
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-accent">
                          15+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Tahun Pengalaman
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Categories Overview */}
                  <div className="bg-base-200 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-6">
                      Kategori Kegiatan
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {conferenceCategories.map((category, index) => (
                        <div
                          key={index}
                          className="bg-base-100 rounded-lg p-5 text-center"
                        >
                          <h4
                            className={`text-lg font-bold ${category.color} mb-3`}
                          >
                            {category.name}
                          </h4>
                          <p className="text-sm text-base-content/70 leading-relaxed">
                            {category.description}
                          </p>
                          <div className="mt-4">
                            <span className="text-xs text-base-content/60">
                              {
                                konferensiInternasionalData.filter(
                                  (conf) => conf.category === category.name,
                                ).length
                              }{" "}
                              kegiatan
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Key Focus Areas */}
                  <div className="bg-base-200 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-6">
                      Area Fokus Konferensi
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🤝</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Kerja Sama Regional
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Penguatan koordinasi kepolisian ASEAN
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">💻</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Kejahatan Siber
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Penanggulangan cybercrime lintas negara
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">👥</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Perdagangan Manusia
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pencegahan trafficking dan people smuggling
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🔒</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Counter Terrorism
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Koordinasi penanggulangan terorisme
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🔫</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Penyelundupan Senjata
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pencegahan arms smuggling regional
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🐾</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Perdagangan Satwa
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Perlindungan wildlife dan timber
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KonferensiInternasional;
